import React, { useCallback, useRef, useEffect, useState } from 'react';
import { parseDateExact, formatDate, parseDate } from './DateHelper';
import { maskInput } from 'vanilla-text-mask';
import { DatePicker } from './DatePicker';
import { startOfToday } from 'date-fns';
import { useLocalization } from './localization';
import { Icon, Icons } from './Icon';
import { useLayoutContext } from '../layout/Layout';

interface DatePickerOptions {
    futureOnly?: boolean;
    pastOnly?: boolean;
    format?: string;
    startDate?: Date;
    endDate?: Date;
}

export interface TextboxChangeEvent {
    currentTarget: { value: string; name?: string };
}
interface TextboxProps {
    value?: string;
    onChange?: (e: TextboxChangeEvent) => void;
    onBlur?: () => void;
    type?: string;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    id?: string;
    name?: string;
    tabIndex?: number;
    mask?: MaskType;
    datepicker?: DatePickerOptions;
    autoComplete?: string;
    maxLength?: number;
    testId?: string;
    disableClipboard?: boolean
    icon?: Icons;
}

type SelectionDirection = 'forward' | 'backward' | 'none';

export type MaskType =
    | 'phone'
    | 'ein'
    | 'ssn'
    | 'last4ssn'
    | 'initials'
    | 'branch-transit'
    | 'bank-institution'
    | 'canada-postal-code'
    | 'card'
    | 'aba-routing-number'
    | 'zip-code';
const getMask = (maskType: MaskType) => {
    switch (maskType) {
        case 'phone':
            return ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /[1-9]/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
        case 'ein':
            return [/\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/];
        case 'ssn':
            return [/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
        case 'last4ssn':
            return [/\d/, /\d/, /\d/, /\d/];
        case 'card':
            return [
                /[\dX]/,
                /[\dX]/,
                /[\dX]/,
                /[\dX]/,
                '-',
                /[\dX]/,
                /[\dX]/,
                /[\dX]/,
                /[\dX]/,
                '-',
                /[\dX]/,
                /[\dX]/,
                /[\dX]/,
                /[\dX]/,
                '-',
                /[\dX]/,
                /[\dX]/,
                /[\dX]/,
                /[\dX]/,
            ];
        case 'initials':
            return [/[a-zA-Z]/, /[a-zA-Z]/, /[a-zA-Z]/];
        case 'branch-transit':
            return [/\d/, /\d/, /\d/, /\d/, /\d/];
        case 'aba-routing-number':
            return [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/];
        case 'bank-institution':
            return [/\d/, /\d/, /\d/];
        case 'canada-postal-code':
            return [/[a-zA-Z]/, /\d/, /[a-zA-Z]/, ' ', /\d/, /[a-zA-Z]/, /\d/];
        case 'zip-code':
            return [/\d/, /\d/, /\d/, /\d/, /\d/];
        default:
            throw new Error(`maskType "${maskType}" is not supported`);
    }
};

const useNativeDatePicker = 'valueAsDate' in document.createElement('input') && window.innerWidth < 600;

const TODAY = startOfToday();

const reformatDate = (value: string, inFormat: string, outFormat: string) => {
    const date = parseDateExact(value, inFormat);
    if (!date) {
        return undefined;
    }
    return formatDate(date, outFormat);
};

export function Textbox(props: TextboxProps) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [focused, setFocused] = useState(false);
    const { mask, datepicker, onChange, tabIndex, disableClipboard, icon } = props;
    const { shortDateFormat } = useLocalization();
    const { model: { isProduction } } = useLayoutContext();

    useEffect(() => {
        if (!mask || !inputRef.current) return;
        const controller = maskInput({
            inputElement: inputRef.current,
            mask: getMask(mask),
        });
        return () => controller.destroy();
    }, [mask]);

    const selection = useRef<[number, number, SelectionDirection]>();
    const tmrHide = useRef(0);

    const disableEvent = (e: any) => {
        e?.preventDefault();
        return false;
    };

    const handleFocus = useCallback(() => {
        clearTimeout(tmrHide.current);
        setFocused(true);
        if (selection.current && inputRef.current && inputRef.current.type) {
            inputRef.current.setSelectionRange(...selection.current);
            selection.current = undefined;
        }
    }, [setFocused, tmrHide]);

    const { onBlur } = props;
    const handleBlur = useCallback(() => {
        if (inputRef.current) {
            selection.current = [
                inputRef.current.selectionStart || 0,
                inputRef.current.selectionEnd || 0,
                (inputRef.current.selectionDirection as SelectionDirection) || 'none',
            ];
        }
        tmrHide.current = window.setTimeout(() => setFocused(false), 100);
        onBlur?.();
    }, [setFocused, onBlur, tmrHide]);

    const handleDateChange = useCallback(
        (date: Date) => {
            if (datepicker && onChange) {
                const newValue = formatDate(date, datepicker.format || shortDateFormat, '');
                onChange({ currentTarget: { value: newValue } });
                if (inputRef.current) {
                    inputRef.current.disabled = true;
                    inputRef.current.disabled = false;
                }
            }
        },
        [onChange, datepicker, inputRef, shortDateFormat]
    );

    const { value, type, placeholder, disabled, className, id, name, maxLength, testId } = props;
    const renderedValue = datepicker && useNativeDatePicker && value ? reformatDate(value, shortDateFormat, 'yyyy-MM-dd') : value;

    const handleRefocus = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [inputRef]);

    return (
        <>
            {icon && (
                <div className="input-group-prepend">
                    <span className="input-group-text click-through">
                        <label onClick={handleRefocus}>
                            <Icon name={icon} />
                        </label>
                    </span>
                </div>
            )}
            {datepicker && !useNativeDatePicker && (
                <DatePicker
                    min={datepicker.startDate || (datepicker.futureOnly ? TODAY : undefined)}
                    max={datepicker.endDate || (datepicker.pastOnly ? TODAY : undefined)}
                    value={parseDate(value)}
                    isOpen={focused}
                    onChange={handleDateChange}
                    onFocusCapture={handleRefocus}
                />
            )}
            <input
                id={id}
                name={name}
                type={type || (datepicker && useNativeDatePicker ? 'date' : 'text')}
                disabled={disabled}
                onInput={onChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onCopy={disableClipboard && isProduction ? disableEvent : undefined}
                onPaste={disableClipboard && isProduction ? disableEvent : undefined}
                onCut={disableClipboard && isProduction ? disableEvent : undefined}
                onDrag={disableClipboard && isProduction ? disableEvent : undefined}
                onDrop={disableClipboard && isProduction ? disableEvent : undefined}
                placeholder={placeholder}
                value={renderedValue ?? ''}
                className={'form-control ' + (className ?? '')}
                autoComplete={datepicker && !useNativeDatePicker ? 'off' : props.autoComplete}
                ref={inputRef}
                tabIndex={tabIndex}
                maxLength={maxLength}
                data-testid={testId ?? id}
            />
        </>
    );
}
